/** Text, a list, or nothing; SCADA items travel as their JSON text. */
export type FieldValue = string | string[] | null;

export const SECRET_NAMES = ['apiKey', 'clientId', 'clientSecret'] as const;
export type SecretName = (typeof SECRET_NAMES)[number];

/** Mirrors the backend's ProviderFieldKind (provider-schemas.ts). */
export const PROVIDER_FIELD_KINDS = [
    'url',
    'host',
    'text',
    'integer',
    'secret',
    'integerList',
    'stringList',
    'identifier',
    'qualifiedName',
    'clarifyItem',
    'enum',
    'date',
    'scadaItems',
] as const;
export type ProviderFieldKind = (typeof PROVIDER_FIELD_KINDS)[number];

export type ProviderFieldScope = 'site' | 'pen';

export interface ProviderFieldSchema {
    key: string;
    scope: ProviderFieldScope;
    kind: ProviderFieldKind;
    required: boolean;
    enumValues: string[];
    labelKey: string;
    helpKey: string | null;
    subject: string | null;
    advanced: boolean;
    max: number | null;
    /** What the fetch uses while the field is empty. */
    defaultValue: string | null;
    /** `machine` for an identifier in a text field (a tenant): shown as machine kinds are. */
    display: ProviderFieldDisplay | null;
}

export type ProviderFieldDisplay = 'machine';

export interface ProviderSchema {
    providerName: string;
    authType: string;
    fields: ProviderFieldSchema[];
}

export interface ConnectionValue {
    /** Null when the pens disagree; `perPen` then holds each pen's value. */
    value: FieldValue;
    varies: boolean;
    perPen: Record<number, FieldValue>;
}

export type CredentialState = 'set' | 'notSet' | 'varies';

export interface CredentialStatus {
    state: CredentialState;
    changedAt: Date | null;
}

/** Only the secrets the provider's schema has are present. */
export type CredentialFlags = Partial<Record<SecretName, boolean>>;

export interface ProviderRowHealth {
    lastFetchedAt: Date | null;
    daysWithData: number;
    hasData: boolean;
}

export interface ProviderRow {
    rowId: number;
    cageId: number;
    isActive: boolean;
    /** Kept as the server sent it, since a save echoes it back as `expectedUpdatedAt`. */
    updatedAt: string;
    fields: Record<string, FieldValue>;
    credentials: CredentialFlags;
    health: ProviderRowHealth;
    /** The row's note, e.g. the external basin the pen maps to. */
    description: string | null;
    /** Stored leaves no schema field reads; OptoScale admins only. */
    storedExtras: StoredExtra[];
    /** Values at a shown field's key that the field cannot read. */
    unreadable: UnreadableValue[];
}

/** A stored JSON leaf by its full path; `value` is JSON text, null when `hidden`. */
export interface StoredExtra {
    path: string;
    value: string | null;
    /** Withheld from a client admin by the server; the page says "Set by OptoScale". */
    hidden: boolean;
}

/**
 * A stored value the field at `key` cannot read; `type` is its JSON type, `value` JSON text,
 * or null for a client admin, whom the server sends the type only.
 */
export interface UnreadableValue {
    key: string;
    type: string;
    value: string | null;
    /** A save cannot write the field: a stored value above it is not an object. */
    blocked: boolean;
}

export interface ProviderGroup {
    providerName: string;
    authType: string;
    connection: Record<string, ConnectionValue>;
    /** Only the secrets the provider's schema has are present. */
    credentials: Partial<Record<SecretName, CredentialStatus>>;
    rows: ProviderRow[];
}

export interface PenRef {
    cageId: number;
    name: string;
}

export interface UnsupportedProvider {
    providerName: string;
    cageIds: number[];
}

export interface LocationProviderConfig {
    locationId: number;
    clientId: number;
    pens: PenRef[];
    providers: ProviderGroup[];
    unsupported: UnsupportedProvider[];
}

/** A string sets the secret, null clears it, an absent name leaves it as stored. */
export type SecretsPatch = Partial<Record<SecretName, string | null>>;

export interface SavePenPatch {
    cageId: number;
    rowId: number | null;
    expectedUpdatedAt: string | null;
    isActive?: boolean;
    fields: Record<string, FieldValue>;
    secrets: SecretsPatch;
    remove?: boolean;
}

export interface SaveProviderConfigInput {
    connection: Record<string, FieldValue>;
    secrets: SecretsPatch;
    pens: SavePenPatch[];
    testToken?: string;
    testOverride?: boolean;
}
